import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface FunktionarEngagement {
    functionText: string;
    startDate: string | null;
    endDate: string | null;
    organizationNumber: string;
    organizationName: string;
    paymentOrders?: string;
}

interface EngagementGanttChartProps {
  data: FunktionarEngagement[];
  minDate?: number;
  maxDate?: number;
  title?: string;
  activeLabel?: string;
  endedLabel?: string;
}

const EngagementGanttChart: React.FC<EngagementGanttChartProps> = ({ data, minDate: propMinDate, maxDate: propMaxDate, title, activeLabel, endedLabel }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!data || data.length === 0) return null;

  // 1. Process data
  const today = new Date();
  
  let domainMin: number;
  let domainMax: number;
  let hasActiveRoles = false;
  
   // Check for active roles
  data.forEach(item => {
      if (item.endDate === null) hasActiveRoles = true;
  });

  if (propMinDate && propMaxDate) {
      domainMin = propMinDate;
      domainMax = propMaxDate;
  } else {
      // Find global min and max dates for the axis
      let minDate = today.getTime();
      let maxDate = 0;

      // First pass to find min/max dates
      data.forEach(item => {
        const start = item.startDate ? new Date(item.startDate).getTime() : today.getTime();
        const end = item.endDate ? new Date(item.endDate).getTime() : today.getTime();
        
        if (start < minDate) minDate = start;
        if (end > maxDate) maxDate = end;
      });

      // If there are active roles, maxDate should be at least today
      if (hasActiveRoles) {
        maxDate = Math.max(maxDate, today.getTime());
      }

      // Add some padding to the axis
      const padding = (maxDate - minDate) * 0.05;
      domainMin = minDate - padding;
      // If active roles, extend to today exactly (or slightly more for label space), otherwise add padding
      domainMax = hasActiveRoles ? today.getTime() : maxDate + padding;
  }

  // Generate ticks for every year
  const ticks: number[] = [];
  const startYear = new Date(domainMin).getFullYear();
  const endYear = new Date(domainMax).getFullYear();

  for (let year = startYear; year <= endYear; year++) {
    const date = new Date(year, 0, 1).getTime();
    if (date >= domainMin && date <= domainMax) {
      ticks.push(date - domainMin);
    }
  }

  // If active roles, fix ticks for "Present"
  if (hasActiveRoles) {
    const todayTick = today.getTime() - domainMin;
    const sixMonths = 1000 * 60 * 60 * 24 * 180;
    const filteredTicks = ticks.filter(t => Math.abs(t - todayTick) > sixMonths);
    filteredTicks.push(todayTick);
    ticks.length = 0;
    ticks.push(...filteredTicks);
    ticks.sort((a, b) => a - b);
  }

  // Group data by organization
  const groupedData: Record<string, FunktionarEngagement[]> = {};
  data.forEach(item => {
    if (!groupedData[item.organizationNumber]) {
      groupedData[item.organizationNumber] = [];
    }
    groupedData[item.organizationNumber].push(item);
  });

  // Sort companies by most recent activity
  const sortedOrgNumbers = Object.keys(groupedData).sort((a, b) => {
    const rolesA = groupedData[a];
    const rolesB = groupedData[b];
    const maxA = Math.max(...rolesA.map(r => r.startDate ? new Date(r.startDate).getTime() : 0));
    const maxB = Math.max(...rolesB.map(r => r.startDate ? new Date(r.startDate).getTime() : 0));
    return maxB - maxA;
  });

  // Flatten data with headers
  const chartData: any[] = [];
  
  sortedOrgNumbers.forEach(orgNum => {
    const roles = groupedData[orgNum];
    roles.sort((a, b) => {
        const startA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const startB = b.startDate ? new Date(b.startDate).getTime() : 0;
        return startB - startA;
    });

    chartData.push({
      id: `header-${orgNum}`,
      type: 'header',
      name: roles[0].organizationName,
      organizationNumber: orgNum,
      offset: 0,
      duration: 0
    });

    roles.forEach((role, index) => {
      const start = role.startDate ? new Date(role.startDate).getTime() : today.getTime();
      const end = role.endDate ? new Date(role.endDate).getTime() : today.getTime();
      
      chartData.push({
        id: `role-${orgNum}-${index}`,
        type: 'role',
        name: role.functionText,
        organizationName: role.organizationName,
        functionText: role.functionText,
        startTime: start,
        endTime: end,
        offset: start - domainMin,
        duration: end - start,
        isCurrent: role.endDate === null
      });
    });
  });

  const truncate = (str: string, n: number) => {
    return (str.length > n) ? str.substr(0, n-1) + '...' : str;
  };

  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const item = chartData.find(row => row.id === payload.value);
    
    if (!item) return null;

    if (item.type === 'header') {
      return (
        <text x={x} y={y} dy={4} textAnchor="end" fill={theme.palette.text.primary} fontWeight="bold" fontSize={11}>
          {truncate(item.name, 35)}
        </text>
      );
    } else {
      return (
        <text x={x} y={y} dy={4} textAnchor="end" fill={theme.palette.text.secondary} fontSize={10}>
          {truncate(item.name, 35)}
        </text>
      );
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      if (data.type === 'header') return null;

      return (
        <Paper sx={{ p: 2, backgroundColor: theme.palette.background.paper, boxShadow: theme.shadows[4] }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {data.organizationName}
          </Typography>
          <Typography variant="body2">
            {data.functionText}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(data.startTime), 'yyyy-MM-dd')} - {data.isCurrent ? t('personSearch.present') : format(new Date(data.endTime), 'yyyy-MM-dd')}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%', height: Math.max(300, chartData.length * 30 + 80), display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.9rem' }}>
        {title ?? t('personSearch.boardTimeline')}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.primary.main, borderRadius: '2px' }} />
          <Typography variant="caption">{activeLabel ?? t('personSearch.activeRole')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.secondary.main, borderRadius: '2px', opacity: 0.7 }} />
          <Typography variant="caption">{endedLabel ?? t('personSearch.endedRole')}</Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
            barCategoryGap={1}
            barGap={0}
          >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
          <XAxis
            type="number"
            domain={[0, domainMax - domainMin]}
            hide
            ticks={ticks}
            tickFormatter={(value) => {
                const date = new Date(value + domainMin);
                if (hasActiveRoles && Math.abs(date.getTime() - today.getTime()) < 1000 * 60 * 60 * 24) {
                    return t('personSearch.present');
                }
                return format(date, 'yyyy');
            }}
          />
          <XAxis
            xAxisId="top"
            orientation="top"
            type="number"
            domain={[0, domainMax - domainMin]}
            ticks={ticks}
            tickFormatter={(value) => {
                const date = new Date(value + domainMin);
                if (hasActiveRoles && Math.abs(date.getTime() - today.getTime()) < 1000 * 60 * 60 * 24) {
                    return t('personSearch.present');
                }
                return format(date, 'yyyy');
            }}
            stroke={theme.palette.text.secondary}
            tick={{ fill: theme.palette.text.secondary, fontSize: 10 }}
          />
          <YAxis
            type="category"
            dataKey="id"
            width={200}
            tick={<CustomYAxisTick />}
            interval={0}
          />
          <Tooltip content={<CustomTooltip />} cursor={{fill: theme.palette.action.hover}} />
          
          <Bar dataKey="offset" stackId="a" fill="transparent" />
          
          <Bar dataKey="duration" stackId="a" radius={[3, 3, 3, 3]} barSize={15}>
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.isCurrent ? theme.palette.primary.main : theme.palette.secondary.main} 
                fillOpacity={entry.isCurrent ? 1 : 0.7}
              />
            ))}
          </Bar>
        </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default EngagementGanttChart;
